import { Component, lazy, Suspense, useEffect, useState } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Gate } from './features/gate/Gate';
import { CharacterSelect } from './features/characters/CharacterSelect';
import { Home } from './features/lobby/Home';
import { Room } from './features/lobby/Room';
// Rarely-visited routes are lazy-loaded so a first-time landing on Home
// doesn't pay for HallOfFame / AchievementsCatalog / Locker / Onboarding
// up front. See performance-audit.md P1.
const HallOfFame = lazy(() => import('./features/hof/HallOfFame').then((m) => ({ default: m.HallOfFame })));
const AchievementsCatalog = lazy(() => import('./features/me/AchievementsCatalog').then((m) => ({ default: m.AchievementsCatalog })));
const Locker = lazy(() => import('./features/locker/Locker').then((m) => ({ default: m.Locker })));
const SeasonPassPanel = lazy(() => import('./features/season/SeasonPassPanel').then((m) => ({ default: m.SeasonPassPanel })));
const LanternShopPanel = lazy(() => import('./features/shop/LanternShopPanel').then((m) => ({ default: m.LanternShopPanel })));
// Phase C3 — Stripe checkout return routes. Lazy so the success-page
// confetti bundle doesn't load on the happy path home.
const PaymentSuccess = lazy(() => import('./features/payments/PaymentSuccess').then((m) => ({ default: m.PaymentSuccess })));
const PaymentCancel = lazy(() => import('./features/payments/PaymentCancel').then((m) => ({ default: m.PaymentCancel })));
const Onboarding = lazy(() => import('./features/onboarding/Onboarding').then((m) => ({ default: m.Onboarding })));
import { Background } from './features/fx/Background';
import { ApiError, releaseCharacter, whoami, joinLobby, getCharacter, type Character, type LobbyTicket } from './lib/api';
import { refreshCharacter } from './lib/refreshCharacter';
import { identifyCharacter, resetIdentity, track } from './lib/analytics';
import { loadRejoin, clearRejoin } from './features/lobby/useLobbySocket';
import { audio, onAudioReady } from '@tt/audio';
import { audioLib } from '@tt/audio-lib';

/**
 * AppErrorBoundary — catches any React render error so the app never
 * shows a literal black screen. Without this, a thrown error in Room
 * (malformed ticket, network race, undefined sock prop, etc.) tears
 * down the entire React tree and the user sees nothing — the
 * "black screen of death" the user kept reporting.
 *
 * Renders a fallback with a Leave button that wipes any rejoin
 * snapshot and reloads to the home route. One click and they're out.
 */
class AppErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  override state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  override componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[AppErrorBoundary]', error, info);
  }
  override render() {
    if (this.state.error) {
      return (
        <div
          className="full-center muted"
          style={{ flexDirection: 'column', gap: 18, padding: 24, textAlign: 'center' }}
        >
          <h2 style={{ margin: 0 }}>Something went wrong.</h2>
          <p style={{ maxWidth: 400, fontSize: 13 }}>{this.state.error.message}</p>
          <button
            type="button"
            className="btn primary"
            onClick={() => {
              try { localStorage.removeItem('tt:rejoin'); } catch { /* */ }
              try { sessionStorage.removeItem('tt:nocon-reloads'); } catch { /* */ }
              window.location.href = '/';
            }}
          >
            Back to home
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

type Stage = 'loading' | 'gate' | 'select' | 'home' | 'room' | 'hof' | 'achievements' | 'locker' | 'season' | 'shop' | 'payment-success' | 'payment-cancel' | 'error';

const ACTIVE_KEY = 'tt:activeChar';

function loadActive(): Character | null {
  try { const raw = localStorage.getItem(ACTIVE_KEY); return raw ? (JSON.parse(raw) as Character) : null; }
  catch { return null; }
}
function saveActive(c: Character | null): void {
  try { if (c) localStorage.setItem(ACTIVE_KEY, JSON.stringify(c)); else localStorage.removeItem(ACTIVE_KEY); }
  catch { /* */ }
}

function urlRoomCode(): string | null {
  const m = /^\/r\/([A-Z]{4})\b/i.exec(window.location.pathname);
  if (!m) return null;
  return m[1]!.toUpperCase();
}

function clearRoomUrl(): void {
  if (window.location.pathname.startsWith('/r/')) {
    window.history.replaceState(null, '', '/');
  }
}

/** Phase C3 — read the Stripe redirect path on mount. Stripe sends users
 *  back to /payment-success?cs={id} or /payment-cancel after the hosted
 *  checkout flow. We capture the cs param and stash both pieces so the
 *  init effect can fork into the payment-result screens before any
 *  whoami/lobby plumbing runs. */
function readPaymentRoute(): { kind: 'success'; cs: string | null } | { kind: 'cancel' } | null {
  const path = window.location.pathname;
  if (path.startsWith('/payment-success')) {
    const cs = new URLSearchParams(window.location.search).get('cs');
    return { kind: 'success', cs };
  }
  if (path.startsWith('/payment-cancel')) return { kind: 'cancel' };
  return null;
}

function clearPaymentUrl(): void {
  const p = window.location.pathname;
  if (p.startsWith('/payment-success') || p.startsWith('/payment-cancel')) {
    window.history.replaceState({}, '', '/');
  }
}

const ONBOARDING_KEY = 'tt:onboardedV2';

export function App() {
  const [stage, setStage] = useState<Stage>('loading');
  const [active, setActive] = useState<Character | null>(null);
  const [ticket, setTicket] = useState<LobbyTicket | null>(null);
  const [pendingCode, setPendingCode] = useState<string | null>(urlRoomCode());
  const [initialSeq, setInitialSeq] = useState<number | undefined>(undefined);
  /** Phase C3 — Stripe Checkout Session ID captured from the
   *  /payment-success?cs=... return URL. Read once on mount and never
   *  re-derived — the init effect uses it to fork into the
   *  payment-result screens. */
  const [paymentRoute] = useState<ReturnType<typeof readPaymentRoute>>(() => readPaymentRoute());
  const [paymentSessionId, setPaymentSessionId] = useState<string | null>(
    paymentRoute && paymentRoute.kind === 'success' ? paymentRoute.cs : null,
  );
  const [showOnboarding, setShowOnboarding] = useState<boolean>(() => {
    try { return !localStorage.getItem(ONBOARDING_KEY); } catch { return false; }
  });
  /** R1#23 — bumping this re-runs the init effect (used by the Retry
   *  button on the 'error' stage). Network blips no longer punt the
   *  user back to the passcode gate. */
  const [retryToken, setRetryToken] = useState(0);
  const [initErrorMsg, setInitErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    switch (stage) {
      case 'gate':   audio.setMood('gate');   audio.setAmbience('storm'); break;
      case 'select': audio.setMood('select'); audio.setAmbience('tavern'); break;
      case 'home':   audio.setMood('home');   audio.setAmbience('tavern'); break;
      case 'room':   audio.setMood('lobby');  audio.setAmbience('tavern'); break;
      case 'hof':    audio.setMood('select'); audio.setAmbience('tavern'); break;
      default: break;
    }
  }, [stage]);

  // Wire @tt/audio-lib to the AudioContext + sfx bus as soon as audio is ready.
  useEffect(() => {
    return onAudioReady((ctx, sfxBus) => audioLib.attach(ctx, sfxBus));
  }, []);

  // Hydrate a11y dataset from localStorage so games can read it on first frame.
  useEffect(() => {
    try {
      const shake = localStorage.getItem('tt:a11y:shake');
      if (shake !== null) document.documentElement.dataset['shake'] = shake;
      const cb = localStorage.getItem('tt:a11y:colorblind');
      if (cb && cb !== 'off') document.documentElement.dataset['cb'] = cb;
    } catch { /* */ }
  }, []);

  // Unlock audio on the FIRST user interaction anywhere on the page —
  // iOS Safari requires the AudioContext to resume in a gesture handler.
  useEffect(() => {
    if (audio.isUnlocked()) return;
    const unlock = () => {
      audio.unlock();
      window.removeEventListener('pointerdown', unlock, true);
      window.removeEventListener('keydown', unlock, true);
    };
    window.addEventListener('pointerdown', unlock, true);
    window.addEventListener('keydown', unlock, true);
    return () => {
      window.removeEventListener('pointerdown', unlock, true);
      window.removeEventListener('keydown', unlock, true);
    };
  }, []);

  useEffect(() => {
    setStage('loading');
    setInitErrorMsg(null);
    let stageDecided = false;
    const setStageOnce = (s: typeof stage) => {
      if (stageDecided) return;
      stageDecided = true;
      setStage(s);
    };
    // Hard guard: if any await below hangs (whoami / joinLobby network
    // stall), stage stays 'loading' forever and the user sees a blank
    // dark screen. After 8s, surface the retryable error stage instead
    // of falling silently to home — that hid network problems and made
    // the "stuck on loading" symptom invisible to ops.
    const stuckTimer = window.setTimeout(() => {
      if (!stageDecided) {
        // eslint-disable-next-line no-console
        console.warn('[App] init stalled after 8s — surfacing retry stage');
        setInitErrorMsg('Could not reach the server. Please try again.');
        setStageOnce('error');
      }
    }, 8_000);
    // Phase C3 — Stripe redirect routing. If the URL says we just came
    // back from a hosted checkout, render the result screen immediately.
    // We still kick off whoami so the active character is hydrated for
    // the post-Continue flow, but the success page polls whoami itself
    // and doesn't depend on the init result.
    if (paymentRoute) {
      window.clearTimeout(stuckTimer);
      // Best-effort character hydration in the background so onContinue
      // lands on Home rather than the picker. Failures fall through to
      // the default home flow when the user clicks Continue.
      void (async () => {
        try {
          const me = await whoami();
          if (me.charId) {
            try {
              const c = loadActive() ?? (await getCharacter(me.charId));
              setActive(c);
              saveActive(c);
            } catch { /* user will land in select after Continue */ }
          }
        } catch { /* */ }
      })();
      setStageOnce(paymentRoute.kind === 'success' ? 'payment-success' : 'payment-cancel');
      return;
    }
    void (async () => {
      try {
        const me = await whoami();
        // Actor cookie is the source of truth for "who is logged in".
        // The localStorage cache is just for fast first paint — if the
        // cookie's charId disagrees with the cached active character,
        // re-authenticate via the picker rather than trust stale state.
        if (!me.charId) {
          // Session cookie valid but no actor cookie → must pick.
          saveActive(null);
          setStageOnce('select');
          return;
        }
        let bound: Character | null = loadActive();
        if (!bound || bound.charId !== me.charId) {
          try {
            bound = await getCharacter(me.charId);
            saveActive(bound);
          } catch {
            saveActive(null);
            setStageOnce('select');
            return;
          }
        }
        setActive(bound);
        const snap = loadRejoin();
        if (snap && snap.charId === bound.charId) {
          try {
            const t = await joinLobby(snap.roomCode);
            setTicket(t);
            setInitialSeq(snap.lastSeq);
            setStageOnce('room');
            return;
          } catch { clearRejoin(); }
        }
        if (pendingCode) {
          try {
            const t = await joinLobby(pendingCode);
            setTicket(t);
            setInitialSeq(undefined);
            setPendingCode(null);
            clearRoomUrl();
            setStageOnce('room');
            return;
          } catch { setPendingCode(null); clearRoomUrl(); }
        }
        setStageOnce('home');
      } catch (e) {
        // R1#23 — distinguish genuine auth failure (cookie expired,
        // missing, OR signature mismatch → must re-enter the gate code)
        // from transient network / 5XX issues. The previous behaviour
        // funnelled everything to the gate, which forced returning
        // users to re-enter their passcode after every network blip.
        //
        // 401 = no Cookie header / bad header.
        // 403 = Cookie header present but the JWT failed verification —
        //   typical after a JWT key rotation, where every previously
        //   issued session cookie is suddenly invalid. Same user remedy
        //   as 401: re-enter the passcode.
        if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
          setStageOnce('gate');
        } else {
          setInitErrorMsg(
            e instanceof ApiError
              ? `Server error (${e.status}). Tap retry to try again.`
              : 'Could not reach the server. Tap retry to try again.',
          );
          setStageOnce('error');
        }
      }
      finally { window.clearTimeout(stuckTimer); }
    })();
    // pendingCode is captured at mount; bumping retryToken re-runs init.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryToken]);

  function onClaimed(c: Character) {
    setActive(c);
    saveActive(c);
    identifyCharacter(c.charId, { name: c.name, totalPoints: c.totalPoints });
    track('character_select', { charId: c.charId, isReturning: !!c.totalPoints });
    if (pendingCode) {
      void (async () => {
        try {
          const t = await joinLobby(pendingCode);
          setTicket(t);
          setInitialSeq(undefined);
          setPendingCode(null);
          clearRoomUrl();
          setStage('room');
        } catch { setPendingCode(null); clearRoomUrl(); setStage('home'); }
      })();
    } else {
      setStage('home');
    }
  }

  async function onSwap() {
    try { await releaseCharacter(); } catch { /* */ }
    resetIdentity();
    setActive(null);
    saveActive(null);
    setStage('select');
  }

  function onEnterLobby(t: LobbyTicket) {
    setTicket(t);
    setInitialSeq(undefined);
    setStage('room');
  }

  function onCharacterUpdated(c: Character) {
    setActive(c);
    saveActive(c);
  }

  /** Phase D — invoked by SeasonPassPanel / LanternShopPanel /
   *  PaymentSuccess after any flow that may have changed lanterns,
   *  inventory, or premium status. Re-fetches /api/whoami +
   *  /api/characters/{id} and overlays both onto the cached Character so
   *  the header pip + IconPicker reflect the new state without a reload. */
  async function onCharacterRefresh(): Promise<void> {
    if (!active) return;
    try {
      const next = await refreshCharacter(active);
      setActive(next);
      saveActive(next);
    } catch {
      // Silent — the panels each have their own refresh paths and the
      // header pip is decorative.
    }
  }

  function onLeaveLobby() {
    setTicket(null);
    setInitialSeq(undefined);
    clearRejoin();
    setStage('home');
  }

  function onDismissOnboarding(): void {
    try { localStorage.setItem(ONBOARDING_KEY, '1'); } catch { /* */ }
    setShowOnboarding(false);
  }

  /** Phase C3 — clears the /payment-success or /payment-cancel URL and
   *  drops the user back into the normal app flow. If a character was
   *  hydrated during the background whoami in the init effect, send them
   *  to home; otherwise the picker. */
  function onLeavePaymentResult(): void {
    clearPaymentUrl();
    setPaymentSessionId(null);
    setStage(active ? 'home' : 'select');
  }

  if (stage === 'loading') {
    return (
      <div className="full-center muted" style={{ flexDirection: 'column', gap: 18 }}>
        <span>…</span>
        {/* Escape valve: if the loading screen is stuck (network stall,
            stale rejoin pointing nowhere, etc.), let the user click out.
            Visible after a small delay so it doesn't flash on every fast
            init. The 8s App-level guard above will also force this, but
            the user-driven path matters when a back-end service hangs. */}
        <button
          type="button"
          className="btn ghost"
          style={{ marginTop: 14, opacity: 0.7 }}
          onClick={() => {
            try { localStorage.removeItem('tt:rejoin'); } catch { /* */ }
            try { sessionStorage.removeItem('tt:nocon-reloads'); } catch { /* */ }
            window.location.href = '/';
          }}
        >
          Tap to leave
        </button>
      </div>
    );
  }

  let screen: React.ReactNode;
  if (stage === 'error') {
    screen = (
      <div
        className="full-center muted"
        style={{ flexDirection: 'column', gap: 16, padding: 24, textAlign: 'center' }}
      >
        <h2 style={{ margin: 0 }}>Trouble reaching the tavern</h2>
        <p style={{ maxWidth: 360, fontSize: 13 }}>
          {initErrorMsg ?? 'Could not reach the server.'}
        </p>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            type="button"
            className="btn primary"
            onClick={() => setRetryToken((n) => n + 1)}
          >
            Retry
          </button>
          <button
            type="button"
            className="btn ghost"
            onClick={() => {
              try { localStorage.removeItem('tt:rejoin'); } catch { /* */ }
              try { sessionStorage.removeItem('tt:nocon-reloads'); } catch { /* */ }
              window.location.href = '/';
            }}
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
  else if (stage === 'gate') screen = <Gate onSuccess={() => setStage(active ? 'home' : 'select')} />;
  else if (stage === 'select') screen = <CharacterSelect onClaimed={onClaimed} />;
  else if (stage === 'hof') screen = <HallOfFame onBack={() => setStage('home')} />;
  else if (stage === 'achievements' && active) screen = <AchievementsCatalog charId={active.charId} onClose={() => setStage('home')} />;
  else if (stage === 'locker' && active) screen = <Locker character={active} onClose={() => setStage('home')} onUpdated={onCharacterUpdated} />;
  else if (stage === 'season' && active) screen = <SeasonPassPanel onClose={() => setStage('home')} onCharacterRefresh={onCharacterRefresh} />;
  else if (stage === 'shop' && active) screen = <LanternShopPanel onClose={() => setStage('home')} onCharacterRefresh={onCharacterRefresh} />;
  else if (stage === 'payment-success') screen = <PaymentSuccess sessionId={paymentSessionId} onContinue={onLeavePaymentResult} onCharacterRefresh={onCharacterRefresh} />;
  else if (stage === 'payment-cancel') screen = <PaymentCancel onContinue={onLeavePaymentResult} />;
  else if (stage === 'home' && active) screen = <Home character={active} onSwap={onSwap} onEnterLobby={onEnterLobby} onOpenHof={() => setStage('hof')} onOpenAchievements={() => setStage('achievements')} onOpenLocker={() => setStage('locker')} onOpenSeason={() => setStage('season')} onOpenShop={() => setStage('shop')} onCharacterUpdated={onCharacterUpdated} onCharacterRefresh={onCharacterRefresh} />;
  else if (stage === 'room' && active && ticket)
    screen = <Room character={active} ticket={ticket} onLeave={onLeaveLobby} onCharacterUpdated={onCharacterUpdated} {...(initialSeq !== undefined ? { initialSeq } : {})} />;
  else screen = (
    <div className="full-center muted" style={{ flexDirection: 'column', gap: 18 }}>
      <span>…</span>
      <button
        type="button"
        className="btn ghost"
        style={{ marginTop: 14, opacity: 0.85 }}
        onClick={() => {
          try { localStorage.removeItem('tt:rejoin'); } catch { /* */ }
          try { sessionStorage.removeItem('tt:nocon-reloads'); } catch { /* */ }
          window.location.href = '/';
        }}
      >
        Tap to leave
      </button>
    </div>
  );

  // Per docs/ui/menu-screens.md §4.6 — each menu screen passes its
  // identity to the shared Background so per-screen accents (a
  // candelabra at the gate, a portrait wall at select, the bar at
  // home) can fade in without remounting the canvas.
  const bgMode: 'default' | 'gate' | 'select' | 'home' | 'locker' | 'onboarding' =
    showOnboarding ? 'onboarding'
    : stage === 'gate' ? 'gate'
    : stage === 'select' ? 'select'
    : stage === 'home' ? 'home'
    : stage === 'locker' ? 'locker'
    : 'default';

  // Skip the menu <Background> when the user is in a room — Room.tsx
  // mounts its own <TavernBackdrop>. Stacking both produces a ghost
  // double-tavern (per user feedback Image #7).
  const showMenuBackground = stage !== 'room';

  return (
    <AppErrorBoundary>
      {showMenuBackground && <Background mode={bgMode} />}
      <Suspense fallback={<div className="full-center muted">…</div>}>
        {screen}
      </Suspense>
      {showOnboarding && stage !== 'gate' && stage !== 'payment-success' && stage !== 'payment-cancel' && (
        <Suspense fallback={null}>
          <Onboarding onDismiss={onDismissOnboarding} />
        </Suspense>
      )}
    </AppErrorBoundary>
  );
}
